import BpkLink from '@skyscanner-internal/backpack-web/bpk-component-link';
import BpkText, {
  TEXT_COLORS,
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import classNames from 'classnames';

import STYLES from './BarChartWithInfoSnippets.module.scss';

export type BarChartItem = {
  name: string;
  stat: string;
  imageUrl?: string;
  percentage: number;
  isHighlighted: boolean;
  copyPlacement?: 'adaptive';
  url?: string;
};

const ADAPTIVE_COPY_INSIDE_THRESHOLD = 50;

const BarCopy = ({
  bar,
  isInsideHighlighted = false,
}: {
  bar: BarChartItem;
  isInsideHighlighted?: boolean;
}) => {
  const textColor = isInsideHighlighted ? TEXT_COLORS.textOnDark : undefined;

  return (
    <div
      className={classNames(STYLES.Bar__copy, {
        [STYLES.Bar__copy_insideHighlighted]: isInsideHighlighted,
      })}
    >
      {bar.url ? (
        <BpkLink
          href={bar.url}
          className={STYLES.Bar__name}
          alternate={isInsideHighlighted}
        >
          {bar.name}
        </BpkLink>
      ) : (
        <BpkText
          textStyle={TEXT_STYLES.label1}
          color={textColor}
          className={STYLES.Bar__name}
        >
          {bar.name}
        </BpkText>
      )}
      <BpkText textStyle={TEXT_STYLES.footnote} color={textColor}>
        {bar.stat}
      </BpkText>
    </div>
  );
};

const Bar = ({ bar }: { bar: BarChartItem }) => {
  const fillClass = bar.isHighlighted
    ? STYLES.Bar__fill_highlighted
    : STYLES.Bar__fill_regular;
  const clampedPercentage = Math.max(0, Math.min(100, bar.percentage));
  const usesAdaptiveCopy = bar.copyPlacement === 'adaptive';
  const isCopyInsideBar =
    usesAdaptiveCopy && clampedPercentage > ADAPTIVE_COPY_INSIDE_THRESHOLD;
  const fillClassName = classNames(fillClass, {
    [STYLES.Bar__fill_adaptive]: usesAdaptiveCopy,
  });

  return (
    <div className={STYLES.Bar}>
      <div className={fillClassName} style={{ width: `${clampedPercentage}%` }}>
        {isCopyInsideBar && (
          <div
            className={classNames(
              STYLES.Bar__copyWrapper,
              STYLES.Bar__copyWrapper_inside,
            )}
          >
            <BarCopy bar={bar} isInsideHighlighted={bar.isHighlighted} />
          </div>
        )}
      </div>
      {!isCopyInsideBar && (
        <div
          className={classNames(STYLES.Bar__logoSection, {
            [STYLES.Bar__logoSection_adaptive]: usesAdaptiveCopy,
          })}
        >
          {bar.imageUrl && (
            <img
              className={STYLES.Bar__image}
              src={bar.imageUrl}
              alt={bar.name}
            />
          )}
          <BarCopy bar={bar} />
        </div>
      )}
    </div>
  );
};

export default Bar;
